/**
 * #4710 — the app comes back from the background showing nothing.
 *
 * A customer switched to another app while a workout was open, came back about 40
 * seconds later, and the screen was blank. It was not a crash and not a reload:
 * the app kept syncing and kept sending telemetry the whole time. Pressing the
 * Android back button cleared it at once. The same person sees it on the installed
 * web app too.
 *
 * The explanation we are acting on — and it IS an explanation, not a proven fact,
 * because nobody here has the device: while the app was away the browser threw
 * away the picture it had drawn. When the app came back it had a fresh, empty
 * surface to draw on. This app only redraws when something actually changes, and
 * on that return nothing had changed, so no new picture was ever drawn and the
 * empty surface stayed on screen until the back press finally changed the page.
 * The full account, the evidence behind it, and the repairs that were measured NOT
 * to work are in `design-docs/navigation.md`, "Whole-page blank after returning to
 * the app" and the "iOS stale-layer rebuild on tab switch" section above it.
 *
 * So this module does two things on every return from a long absence:
 *
 *  - THE REPAIR. Ask the page to redraw itself, by hiding it for two drawn frames
 *    and putting it back — `hidePageForTwoDrawnFramesAsync` below. The adapter
 *    supplies the page and the frame wait; the rules live here.
 *  - THE MEASUREMENT. Ask the browser for one frame at the moment of return and
 *    see how long it takes to arrive. That is the closest thing to "did anything
 *    get drawn?" the web platform offers — there is no API that reports what was
 *    actually painted.
 *
 * The ORDER of those two is load-bearing and is the whole reason for
 * `REPAINT_DELAY_MS`. The repair's own change to the page produces a frame. If the
 * repair ran first, the frame would arrive immediately every single time, and the
 * measurement would record every return as healthy — including the broken ones the
 * repair had just fixed. So the frame is asked for first, and the repair waits.
 *
 * Everything here is plain TypeScript with no Angular and no DOM, so the unit-test
 * runner can load it. `resume-repaint.service.ts` is the adapter that supplies the
 * real browser. Same split, for the same reason, as `connectivity-resume.ts`.
 */

/** How long the app must have been away before any of this runs.
 *
 * Short hide/show flips are ordinary and common — in a two-day sample, 13 of 34
 * users produced a pair of resume events within 2 seconds of each other. Repairing
 * on every one of them would put needless work in front of routine tab switching,
 * and would drown the measurement in cases nobody cares about. The reported failure
 * followed a 40-second absence, so a threshold in seconds keeps every case we are
 * chasing and drops the noise. */
export const MIN_HIDDEN_MS = 5000;

/** How long the repair waits after the return, so the measurement can see the
 *  untouched state first. See the ordering note above. 150 ms added to a blank
 *  screen that has already lasted seconds is not noticeable. */
export const REPAINT_DELAY_MS = 150;

/** When the outcome is decided, measured from the return. */
export const CLASSIFY_AFTER_MS = 2000;

/** How much longer a browser that has drawn nothing by then is given, before we
 *  say it really has stopped. See `startConfirmation` for why this exists. */
export const CONFIRMATION_WINDOW_MS = 1000;

/** A frame slower than this is worth telling somebody about.
 *
 * Below it we record the number but stay quiet, because the frame callback and the
 * timer that measures it run on the same thread: when that thread is busy, BOTH are
 * late, and ordinary slowness looks exactly like a browser that has stopped
 * drawing. This app does a reachability probe and restarts two sync connections on
 * every return, so a few hundred milliseconds of that is normal, not a fault. */
export const LATE_FRAME_REPORT_MS = 1000;

/** Hide the page for two drawn frames so the browser has to redraw it.
 *
 * This is the repair. It takes the two things it needs as arguments rather than
 * reaching for the page itself, so it has no DOM in it and the unit-test runner
 * can load it — which matters, because both of its rules are easy to get wrong
 * and impossible to notice afterwards.
 *
 * TWO waits, not one, and that is the difference between this working and doing
 * nothing at all. An animation-frame callback runs at the START of a frame,
 * before that frame is painted. So after a single wait the browser has still not
 * drawn the hidden page: the restore lands first and the frame paints with
 * nothing changed. Two waits put a fully drawn frame in between, which is what
 * makes the browser repaint. `cs-tab-content` waits twice for the same reason
 * (#4410).
 *
 * The restore is in a `finally` because leaving the page hidden would be a
 * permanent blank screen of our own making — out of a fix for a blank screen.
 * The caller's wait must also be capped, since a browser stops drawing the
 * moment the app goes off screen and it can go off screen again moments after
 * coming back.
 *
 * @param setVisibility called with `'hidden'` to hide the page and `''` to give
 *   it back whatever the stylesheet says.
 * @param waitForDrawnFrameAsync waits for one frame the browser actually drew.
 *   Must be capped by the caller; see above.
 */
export async function hidePageForTwoDrawnFramesAsync(
  setVisibility: (value: string) => void,
  waitForDrawnFrameAsync: () => Promise<void>,
): Promise<void> {
  setVisibility('hidden');
  try {
    await waitForDrawnFrameAsync();
    await waitForDrawnFrameAsync();
  } finally {
    setVisibility('');
  }
}

/** What the browser did after the app came back. */
export type ResumeFrameOutcome =
  /** A frame arrived before the repair even had its turn. Nothing to see. */
  | 'drew-promptly'
  /** A frame arrived, but late. Whether that is a fault depends on how late — and
   *  `repaint_applied` says whether the repair was in play when it arrived. */
  | 'drew-late'
  /** Nothing by the deadline, but something arrived in the extra window. */
  | 'drew-after-confirmation'
  /** Nothing at all, and the app was on screen the whole time. */
  | 'never-drew'
  /** The app went away again before we could tell. A page that is off screen is
   *  SUPPOSED to stop drawing, so this is not a fault and is never reported. */
  | 'hidden-again';

/** Why the repair did not run. Absent when it did. */
export type RepaintSkippedReason = 'not-visible' | 'page-transition';

export type ResumeRepaintTimerHandle = number;

/** Everything this flow needs from the outside world. */
export interface ResumeRepaintPorts {
  /** A monotonic millisecond clock, used only to measure how long things took. */
  nowMs(): number;
  /** Is the app on screen right now? */
  isDocumentVisible(): boolean;
  /** Is a page slide running? The repair is skipped while one is. */
  isPageTransitionRunning(): boolean;
  /** Ask the browser for one drawn frame. `onFrame` runs when it draws one. */
  requestFrame(onFrame: () => void): void;
  /** Run `action` after `delayMs`. */
  setTimer(delayMs: number, action: () => void): ResumeRepaintTimerHandle;
  clearTimer(handle: ResumeRepaintTimerHandle): void;
  /** Hide the page for two drawn frames and put it back, so the browser has to
   *  redraw it. The adapter finds the page and supplies a capped frame wait;
   *  `hidePageForTwoDrawnFramesAsync` above is what it calls, and that is where
   *  the two-frame rule and the always-restore guarantee live. */
  repaintActivePageAsync(): Promise<void>;
  trackEvent(name: string, properties: Record<string, unknown>): void;
  trackSilentProblem(name: string, context: Record<string, unknown>): void;
}

/** One return being watched. */
interface CheckInProgress {
  startedAtMs: number;
  hiddenForMs: number;
  firstFrameAtMs?: number;
  repaintApplied: boolean;
  repaintSkippedReason?: RepaintSkippedReason;
  /** True once we are in the extra window described on CONFIRMATION_WINDOW_MS. */
  confirming: boolean;
  timers: ResumeRepaintTimerHandle[];
}

export class ResumeRepaintFlow {
  /** When the page last went off screen. Undefined means it has not, as far as we
   *  know — `onVisible` can arrive with no `onHidden` behind it, either because
   *  the listener was attached while the page was already hidden, or because
   *  `onHidden` refused a page that had not really gone away. */
  private hiddenAtMs?: number;

  private check?: CheckInProgress;

  constructor(private ports: ResumeRepaintPorts) { }

  /** The app went off screen. */
  onHidden(): void {
    // if the page is still on screen, this was not a real absence — refuse it.
    // The browser only throws away its picture when the page genuinely goes away,
    // so repairing here would hide and redraw a page nothing was wrong with, and
    // would make `hidden_ms` count time the app was merely unfocused.
    if (this.ports.isDocumentVisible()) return;
    this.hiddenAtMs = this.ports.nowMs();
  }

  /** The app came back on screen. */
  onVisible(): void {
    const hiddenAtMs = this.hiddenAtMs;
    this.hiddenAtMs = undefined;
    // never away, or away only briefly: leave the app alone entirely
    if (hiddenAtMs === undefined) return;
    const hiddenForMs = this.ports.nowMs() - hiddenAtMs;
    if (hiddenForMs < MIN_HIDDEN_MS) return;

    // a second return replaces the first: two overlapping checks would report
    // twice for one return and would both reach for the same page element
    this.abandonCheck();

    const check: CheckInProgress = {
      startedAtMs: this.ports.nowMs(),
      hiddenForMs,
      repaintApplied: false,
      confirming: false,
      timers: [],
    };
    this.check = check;

    // the measurement goes first — see the ordering note at the top of the file
    this.ports.requestFrame(() => this.noteFrameDrawn(check));
    check.timers.push(this.ports.setTimer(REPAINT_DELAY_MS, () => this.runRepair(check)));
    check.timers.push(this.ports.setTimer(CLASSIFY_AFTER_MS, () => this.classify(check)));
  }

  /** Drop an in-flight check without reporting it. */
  private abandonCheck(): void {
    if (!this.check) return;
    for (const timer of this.check.timers) this.ports.clearTimer(timer);
    this.check = undefined;
  }

  /** The browser drew a frame. */
  private noteFrameDrawn(check: CheckInProgress): void {
    // a frame belonging to a check we have already finished or replaced
    if (this.check !== check) return;
    if (check.firstFrameAtMs === undefined) {
      check.firstFrameAtMs = this.ports.nowMs();
    }
    // if we were in the extra window waiting for exactly this, we have our answer
    if (check.confirming) {
      this.finish(check, 'drew-after-confirmation');
    }
  }

  /** Ask the page to redraw itself, unless there is a reason not to. */
  private runRepair(check: CheckInProgress): void {
    if (this.check !== check) return;

    if (!this.ports.isDocumentVisible()) {
      // the app left again while we waited. Redrawing a page nobody can see would
      // achieve nothing, and the browser would not draw the frame anyway.
      check.repaintSkippedReason = 'not-visible';
      return;
    }
    if (this.ports.isPageTransitionRunning()) {
      // a page vanishing and reappearing for a frame mid-slide looks like a
      // glitch. A return landing mid-slide is rare, so skipping costs nothing.
      check.repaintSkippedReason = 'page-transition';
      return;
    }

    check.repaintApplied = true;
    // deliberately not awaited: the measurement's clock is already running, and
    // the repair must never be able to hold the rest of this up. The adapter
    // guarantees the page is restored, but a page element that another part of
    // the app removed underneath us can still make this throw, and that must
    // surface rather than becoming an unhandled rejection.
    void this.ports.repaintActivePageAsync().catch((error: unknown) => {
      this.ports.trackSilentProblem('ResumeRepaintFailed', {
        message: (error as Error)?.message ?? String(error),
      });
    });
  }

  /** Decide what happened, or give a silent browser one more chance. */
  private classify(check: CheckInProgress): void {
    if (this.check !== check) return;

    if (check.firstFrameAtMs !== undefined) {
      const tookMs = check.firstFrameAtMs - check.startedAtMs;
      this.finish(check, tookMs < REPAINT_DELAY_MS ? 'drew-promptly' : 'drew-late');
      return;
    }

    if (!this.ports.isDocumentVisible()) {
      this.finish(check, 'hidden-again');
      return;
    }

    this.startConfirmation(check);
  }

  /** Nothing has been drawn yet. Ask once more before calling it broken.
   *
   * Without this, one long main-thread stall landing at exactly the wrong moment
   * is indistinguishable from a browser that has genuinely stopped drawing, and
   * every slow phone and every busy build machine would file a false report. */
  private startConfirmation(check: CheckInProgress): void {
    check.confirming = true;
    this.ports.requestFrame(() => this.noteFrameDrawn(check));
    check.timers.push(this.ports.setTimer(CONFIRMATION_WINDOW_MS, () => {
      if (this.check !== check) return;
      // still nothing. If the app left in the meantime that explains it.
      this.finish(check, this.ports.isDocumentVisible() ? 'never-drew' : 'hidden-again');
    }));
  }

  /** Report the outcome and close the check. */
  private finish(check: CheckInProgress, outcome: ResumeFrameOutcome): void {
    for (const timer of check.timers) this.ports.clearTimer(timer);
    this.check = undefined;

    const firstFrameMs = check.firstFrameAtMs === undefined
      ? undefined
      : check.firstFrameAtMs - check.startedAtMs;

    // every property spelled out as a literal key, including the two that are
    // often undefined. The telemetry-lexicon gate reads this object to check the
    // event against datasrc/mixpanel-lexicon-event-properties.csv, and it can
    // only do that when the keys are written plainly rather than spread in.
    this.ports.trackEvent('resume_frame_check', {
      frame_outcome: outcome,
      hidden_ms: check.hiddenForMs,
      first_frame_ms: firstFrameMs,
      repaint_applied: check.repaintApplied,
      repaint_skipped_reason: check.repaintSkippedReason,
    });

    if (outcome === 'never-drew') {
      this.ports.trackSilentProblem('ResumeNeverDrewAfterReturn', {
        hidden_ms: check.hiddenForMs,
        repaint_applied: check.repaintApplied,
        repaint_skipped_reason: check.repaintSkippedReason,
      });
      return;
    }

    if (outcome === 'drew-late' && firstFrameMs !== undefined && firstFrameMs >= LATE_FRAME_REPORT_MS) {
      this.ports.trackSilentProblem('ResumeDrewLateAfterReturn', {
        hidden_ms: check.hiddenForMs,
        first_frame_ms: firstFrameMs,
        // the interesting combination: drawing only restarted once the repair
        // touched the page. That is this whole theory being right.
        repaint_applied: check.repaintApplied,
      });
    }
  }
}
